// ABOUTME: Login page form content for email/password and NFC authentication.
// ABOUTME: Renders inside AuthLayout which provides the shared shell (logo, controls, OnTheRight).
import { Box, ButtonBase, IconButton, InputBase, Typography, useTheme } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Formik } from "formik";
import { useEffect, useState, useRef } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import * as yup from "yup";
import Shbutton from "../../components/theme/buttons/Shbutton";
import Text from "../../components/theme/text/Text";

import LockIcon from '@mui/icons-material/Lock';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import cx from 'classnames';
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ENV_TYPES } from "../../components/global/Sidebar";
import { resetSideBar, setSelectedEnvironment } from "../../newapi/global/settingsSlice";
import { updateActive } from "../../newapi/warehouse/warehouseSlice";
import { persistor } from "../../newapi/store";
import { useLoginMutation } from "../../newapi/user/authentication/authApiSlice";
import { hasPermissionForCustomer, relationBlocked, selectAuth, selectCustomers, selectRelations, selectUserPermission, UserState } from "../../newapi/user/authentication/authSlice";
import { useCheckWhitelistLoginQuery } from "../../newapi/user/whitelistSlice";
import { tokens } from "../../theme";
import WifiTetheringOutlinedIcon from '@mui/icons-material/WifiTetheringOutlined';
import { UserPrivileges } from "../../utils/permissions";
import MfaChallenge from "./MfaChallenge";
// import { login } from "../../api/global/user";
// import { useAuth } from "../../authentication/AuthState";
const initialValues = {
    email: "",
    password: "",
}

const initialValuesNfc = {
    employee_code: "",
}

const userSchema = yup.object().shape({
    email: yup
    .string()
    .email("Ongeldig e-mailadres")
    .required("required"),
    password: yup.string().required("required"),
});

const userSchemaNfc = yup.object().shape({
    employee_code: yup.string().required("required"),
});

const Login = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const {t} = useTranslation();
    const [errorMessage, setErrorMessage] = useState("");
    // const signIn = useSignIn();
    const navigate = useNavigate();
    const isNonMobile = useMediaQuery("(min-width:600px)");
    const [passwordType, setPasswordType] = useState("password");

    const { isOnline, brand, brandSlug } = useOutletContext();

    const togglePasswordVisibility = () => {
        setPasswordType(prev => (prev === "password" ? "text" : "password"));
    };

    const [nfc, setNfc] = useState(false);
    const [showNfc, setShowNfc] = useState(false);
    const [nfcInputTimeout, setNfcInputTimeout] = useState(null);
    const [mfaRequired, setMfaRequired] = useState(false);
    const [mfaToken, setMfaToken] = useState(null);
    const nfcInputRef = useRef(null);

    // useLoginMutation
    const [login, {isSuccess, isLoading,isUninitialized, isError,error }] = useLoginMutation();

    const customers = useSelector(selectCustomers);
    const relations = useSelector(selectRelations);

    const permissions = useSelector(state => state.auth.permissions);
    const warehouseId = useSelector(state => state.warehouse.activeId);
    const customerWarehouses = useSelector(state => state.auth.customerWarehouses);

    const auth = useSelector(selectAuth);
    const blocked = useSelector(relationBlocked);

    const { data: whitelistData, isLoading: whitelistLoading, isError: whitelistError } = useCheckWhitelistLoginQuery();

    useEffect(() => {
        if(whitelistData) {
            setNfc(whitelistData.data);
            setShowNfc(whitelistData.data);
        }
    },[whitelistData]);

    const dispatch = useDispatch();

    const handleFormSubmit = async (values) => {
        setErrorMessage("");

        let loginvals = {email: values.email, password: values.password, employee_code: values.employee_code, customer: "avantibv",
        customer_id: 1};

        try {
            const result = await login(loginvals).unwrap();
            if (result?.data?.mfaRequired) {
                setMfaToken(result.data.mfaToken);
                setMfaRequired(true);
                return;
            }
        } catch (error) {
            console.error('Login failed:', error.message);
        }
    }

    const handleMfaBack = () => {
        setMfaRequired(false);
        setMfaToken(null);
        setErrorMessage("");
    };

    useEffect(() => {
        if(isError) {
            setErrorMessage("Login niet gelukt");
            return;
        }

        if(isSuccess || (mfaRequired && auth.status === UserState.LOGGED_IN)) {
            // If MFA is required but auth isn't complete yet, wait
            if(!customers || !relations) {
                return;
            }
            //set selected environment
            if(blocked){
                navigate("/blocked");
                return;
            }

            // Handle pending bol.com OAuth redirect
            const bolState = localStorage.getItem("bol_oauth_state");
            const bolCode = localStorage.getItem("bol_oauth_code");
            const bolExpires = localStorage.getItem("bol_oauth_expires");
            const bolPending = (bolState || bolCode) && (!bolExpires || new Date() < new Date(bolExpires));

            if (bolPending) {
                localStorage.removeItem("bol_oauth_state");
                localStorage.removeItem("bol_oauth_code");
                localStorage.removeItem("bol_oauth_expires");

                const bolParams = `type=bol${bolState ? `&state=${encodeURIComponent(bolState)}` : ''}${bolCode ? `&code=${encodeURIComponent(bolCode)}` : ''}`;

                if (relations.length > 0) {
                    dispatch(setSelectedEnvironment({
                        selectedId: relations[0].id,
                        selectedEnvironmentType: ENV_TYPES[200],
                        selectedMenuItem: "relation-integrations-saleschannels",
                        relationCustomerId: relations[0].customers[0].id,
                        relationRootId: relations[0].internalId
                    }));
                    navigate(`/relation/integrations/saleschannels?${bolParams}`);
                    return;
                } else if (customers.length > 0 && !customers[0].isFulfilment) {
                    const customer = customers[0];
                    const foundWarehouseId = customerWarehouses?.find(x => x.customerId === customer.id)?.warehouseId ?? null;
                    dispatch(setSelectedEnvironment({
                        selectedEnvironmentType: ENV_TYPES[100],
                        selectedId: customer.id,
                        isFulfilment: customer.isFulfilment,
                        selectedMenuItem: "warehouse-settings-integrations-saleschannels",
                        relationCustomerId: 0,
                        relationRootId: 0
                    }));
                    if (foundWarehouseId) {
                        dispatch(updateActive({ id: foundWarehouseId, name: '' }));
                    }
                    navigate(`/warehouse/settings/integrations/saleschannels?${bolParams}`);
                    return;
                }
            }

            // Handle pending TikTok Shop OAuth redirect
            const tiktokCode = localStorage.getItem("tiktok_oauth_code");
            const tiktokRegion = localStorage.getItem("tiktok_oauth_region");
            const tiktokExpires = localStorage.getItem("tiktok_oauth_expires");
            const tiktokPending = tiktokCode && (!tiktokExpires || new Date() < new Date(tiktokExpires));

            if (tiktokPending) {
                localStorage.removeItem("tiktok_oauth_code");
                localStorage.removeItem("tiktok_oauth_region");
                localStorage.removeItem("tiktok_oauth_expires");

                const tiktokParams = `type=tiktokshop&code=${encodeURIComponent(tiktokCode)}${tiktokRegion ? `&shop_region=${encodeURIComponent(tiktokRegion)}` : ''}`;

                if (relations.length > 0) {
                    dispatch(setSelectedEnvironment({
                        selectedId: relations[0].id,
                        selectedEnvironmentType: ENV_TYPES[200],
                        selectedMenuItem: "relation-integrations-saleschannels",
                        relationCustomerId: relations[0].customers[0].id,
                        relationRootId: relations[0].internalId
                    }));
                    navigate(`/relation/integrations/saleschannels?${tiktokParams}`);
                    return;
                } else if (customers.length > 0 && !customers[0].isFulfilment) {
                    const customer = customers[0];
                    const foundWarehouseId = customerWarehouses?.find(x => x.customerId === customer.id)?.warehouseId ?? null;
                    dispatch(setSelectedEnvironment({
                        selectedEnvironmentType: ENV_TYPES[100],
                        selectedId: customer.id,
                        isFulfilment: customer.isFulfilment,
                        selectedMenuItem: "warehouse-settings-integrations-saleschannels",
                        relationCustomerId: 0,
                        relationRootId: 0
                    }));
                    if (foundWarehouseId) {
                        dispatch(updateActive({ id: foundWarehouseId, name: '' }));
                    }
                    navigate(`/warehouse/settings/integrations/saleschannels?${tiktokParams}`);
                    return;
                }
            } else if (tiktokCode) {
                // Stashed code has expired (or no relation/customer to resume into) — clear it
                // rather than leaving a stale code around for a future login attempt.
                localStorage.removeItem("tiktok_oauth_code");
                localStorage.removeItem("tiktok_oauth_region");
                localStorage.removeItem("tiktok_oauth_expires");
            }

            // Honour the user's chosen main environment when it still resolves to
            // one they have access to. Falls through to the default landing below
            // when unset or stale (e.g. the environment was revoked).
            const defaultEnvironment = auth.user?.defaultEnvironment;
            if (defaultEnvironment) {
                if (defaultEnvironment.type === ENV_TYPES[100]) {
                    const customerIndex = customers.findIndex(c => c.id === defaultEnvironment.id);
                    if (customerIndex !== -1) {
                        const customer = customers[customerIndex];
                        const foundWarehouseId = customerWarehouses?.find(x => x.customerId === customer.id)?.warehouseId ?? null;
                        const canAccessDashboard = hasPermissionForCustomer({
                            permissions,
                            customerId: customer.id,
                            warehouseId,
                            requiredPermissions: [UserPrivileges.DASHBOARD_READ]
                        });

                        dispatch(setSelectedEnvironment({
                            selectedEnvironmentType: ENV_TYPES[100],
                            selectedId: customer.id,
                            selectedEnvironmentIndex: customerIndex + 1,
                            isFulfilment: customer.isFulfilment,
                            selectedMenuItem: canAccessDashboard ? "warehouse-dashboard" : "warehouse-outbound-orders",
                            relationCustomerId: 0,
                            relationRootId: 0
                        }));

                        if (foundWarehouseId) {
                            dispatch(updateActive({ id: foundWarehouseId, name: '' }));
                        }

                        if (canAccessDashboard) {
                            navigate("/warehouse/dashboard");
                        } else {
                            navigate("/warehouse/outbound/orders");
                        }
                        return;
                    }
                } else if (defaultEnvironment.type === ENV_TYPES[200]) {
                    const relationIndex = relations.findIndex(r => r.internalId === defaultEnvironment.id);
                    if (relationIndex !== -1) {
                        const relation = relations[relationIndex];

                        dispatch(setSelectedEnvironment({
                            selectedId: relation.id,
                            selectedEnvironmentType: ENV_TYPES[200],
                            selectedEnvironmentIndex: customers.length + relationIndex + 1,
                            selectedMenuItem: "relation-dashboard",
                            relationCustomerId: relation.customers[0].id,
                            relationRootId: relation.internalId
                        }));

                        if (localStorage.getItem("shopify_installation_shop") ||
                            localStorage.getItem("amazon_installation_callback_uri") ||
                            localStorage.getItem("tiktok_oauth_code")) {
                            navigate("/relation/integrations/saleschannels?view=all");
                        } else {
                            navigate("/relation/dashboard");
                        }
                        return;
                    }
                }
            }

            if(customers.length > 0) {
                const customer = customers[0];

                // Check for active warehouseId
                const foundWarehouseId = customerWarehouses?.find(x => x.customerId === customer.id)?.warehouseId ?? null;

                const canAccessDashboard = hasPermissionForCustomer({
                    permissions,
                    customerId: customer.id,
                    warehouseId,
                    requiredPermissions: [UserPrivileges.DASHBOARD_READ]
                });

                dispatch(setSelectedEnvironment({
                    selectedEnvironmentType: ENV_TYPES[100],
                    selectedId: customer.id,
                    isFulfilment: customer.isFulfilment,
                    selectedMenuItem: canAccessDashboard ? "warehouse-dashboard" : "warehouse-outbound-orders",
                    relationCustomerId: 0,
                    relationRootId: 0
                }));

                if (foundWarehouseId) {
                    dispatch(updateActive({ id: foundWarehouseId, name: '' }));
                }

                if (canAccessDashboard) {
                    navigate("/warehouse/dashboard");
                } else {
                    navigate("/warehouse/outbound/orders");
                }

            } else if(relations.length > 0) {
                dispatch(setSelectedEnvironment({
                    selectedId: relations[0].id,
                    selectedEnvironmentType: ENV_TYPES[200],
                    selectedMenuItem: "relation-dashboard",
                    relationCustomerId: relations[0].customers[0].id,
                    relationRootId: relations[0].internalId
                }));


                if (localStorage.getItem("shopify_installation_shop") ||
                    localStorage.getItem("amazon_installation_callback_uri") ||
                    localStorage.getItem("tiktok_oauth_code")) {
                    navigate("/relation/integrations/saleschannels?view=all");
                } else {
                    navigate("/relation/dashboard");
                }
            } else {
                //No relations or customers
                dispatch(setSelectedEnvironment({
                    selectedEnvironmentType: ENV_TYPES[0],
                    selectedEnvironmentIndex: 0,
                    selectedMenuItem: "account-general",
                    relationCustomerId: 0,
                    relationRootId: 0
                }));

                navigate("/user/account/general");
            }

        }
    },[isSuccess, isError, blocked, mfaRequired, customers, relations, auth.status]);

    useEffect(() => {
        if (nfcInputTimeout) {
            clearTimeout(nfcInputTimeout);
        }
    }, [nfcInputTimeout]);

    useEffect(() => {
        dispatch(resetSideBar());
            persistor.purge().then(() => {
        });
    },[])

    useEffect(() => {
        if (nfc && nfcInputRef.current) {
            nfcInputRef.current.focus();
        }
    }, [nfc, nfcInputRef]);

    return (
        <>
            {mfaRequired ? (
                <MfaChallenge mfaToken={mfaToken} onBack={handleMfaBack} brand={brand} isOnline={isOnline} />
            ) : nfc ? (
                <>
                    <Box
                        sx={{
                            padding: "32px",
                            borderRadius: "24px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            position: "relative",
                            paddingTop:"40px",
                            "@media screen and (max-width: 47.9375em)": {
                                p:0,
                                px:3,
                            }
                        }}
                    >

                        <Typography variant="h1" fontWeight="1000" fontFamily="Roboto, sans-serif"
                            sx={{
                                color:colors.txt['primary'],
                                textOverflow:"ellipsis",
                                whiteSpace:"nowrap",
                                overflow:"hidden",
                                fontSize:"32px",
                                "@media screen and (max-width: 47.9375em)" : {
                                    fontSize:21
                                }
                            }}
                        >
                            {t("login_with_nfc")}
                        </Typography>
                        <Typography variant="p" fontWeight="400" fontFamily="Roboto, sans-serif"
                            sx={{
                                color:colors.txt['secondary'],
                                textOverflow:"ellipsis",
                                whiteSpace:"nowrap",
                                overflow:"hidden",
                                fontSize:"17px",
                                marginBottom:4,
                                "@media screen and (max-width: 47.9375em)" : {
                                    fontSize:14,
                                    marginBottom:3,
                                }
                            }}
                        >
                            {t("scan_nfc_to_login")}
                        </Typography>

                        <Box
                            sx={{
                                "@media screen and (max-width: 47.9375em)" : {
                                    width:1,
                                }
                            }}
                        >
                            {isError &&
                                <Box
                                    sx={{
                                        color:colors.red[400],
                                        background:colors.red[100],
                                        border:`1px solid ${colors.red[400]}`,
                                        p:1,
                                        textAlign:'center',
                                        fontFamily:"Roboto,sans-serif",
                                        mb:4,
                                        borderRadius:5,
                                        fontSize:14,
                                        width:"450px"
                                    }}
                                >
                                    {error?.data?.error}
                                </Box>
                            }
                            <Formik
                                key="nfc-mode"
                                enableReinitialize
                                onSubmit={handleFormSubmit}
                                initialValues={initialValuesNfc}
                                validationSchema={userSchemaNfc}>
                                {({values, errors, touched, handleBlur, handleChange, handleSubmit}) => (
                                    <form className="login-form" onSubmit={handleSubmit}>
                                        <Box
                                            display="flex"
                                            sx={{
                                                width:"450px",
                                                borderRadius:6,
                                                display:"flex",
                                                alignItems:"center",
                                                background:theme.palette.mode === "dark" ? colors.grey[400]:colors.grey[100],
                                                border:theme.palette.mode === "dark" ? null : "1px solid rgba(255,255,255,.15)",
                                                height:48,
                                                pl:2,
                                                "&.error": {
                                                    background:colors.red[100],
                                                    border: `1px solid ${colors.red[400]}`,
                                                    color: colors.red[400]
                                                },
                                                "input:-webkit-autofill,input:-webkit-autofill:hover,input:-webkit-autofill:focus,input:-webkit-autofill:active" : {
                                                    backgroundClip:colors.txt['primary'],
                                                    "webkit-text-fill-color":colors.txt['primary'],
                                                    transition: "background-color 5000s ease-in-out 0s",
                                                    boxShadow:`inset 0 0 20px 20px ${theme.palette.mode === "dark" ? colors.grey[400]:colors.grey[100]}`,
                                                    WebkitTextFillColor: colors.txt['primary'],
                                                    fontSize:16
                                                },
                                                "@media screen and (max-width: 64em)" : {
                                                    height:40,
                                                    borderRadius:5,
                                                    px:1,
                                                },
                                                "@media screen and (max-width: 47.9375em)" : {
                                                    width:1,
                                                }
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    height:32,
                                                    width:32,
                                                    textAlign:"center",
                                                    color:colors.txt['secondary'],
                                                    display:"flex",
                                                    alignItems:"center",
                                                    justifyContent:"center",
                                                }}
                                            >
                                                <WifiTetheringOutlinedIcon
                                                    // color={!!touched.email && !!errors.email ? "red" : "primaryGrey"}

                                                />

                                            </Box>
                                            <InputBase
                                                name="nfc-input"
                                                inputRef={nfcInputRef}
                                                autoFocus={nfc}
                                                type={"text"}
                                                sx={{
                                                    pl:1.5,
                                                    flex:1,
                                                    color:colors.txt['primary'],
                                                    fontSize:16
                                                }}
                                                autoComplete="off"
                                                placeholder={t("put_tag_on_reader")}
                                                value={values.employee_code}
                                                onBlur={handleBlur}
                                                onChange={(e, event) => {
                                                    handleChange({ ...event, target: { name: 'employee_code', value: e.target.value } });
                                                    // Clear any existing timeout
                                                    if (nfcInputTimeout) {
                                                        clearTimeout(nfcInputTimeout);
                                                    }
                                                    // Set new timeout for 0.5 seconds
                                                    const timeout = setTimeout(() => {
                                                        handleSubmit();
                                                    }, 500);
                                                    setNfcInputTimeout(timeout);
                                                }}

                                                />
                                        </Box>

                                    </form>
                                )}
                            </Formik>
                        </Box>

                        <Box
                            sx={{
                                display:"flex",
                                alignItems:"center",
                                gap:2,
                                marginTop:4,
                                width:"450px",
                                "@media screen and (max-width: 47.9375em)" : {
                                    width:1,
                                }
                            }}
                        >
                            <Box sx={{ flex:1, height:"1px", background:theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[200] }} />
                            <Text light>{t("or_login_with")}</Text>
                            <Box sx={{ flex:1, height:"1px", background:theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[200] }} />
                        </Box>
                        <Box
                            sx={{
                                display:"flex",
                                justifyContent:"flex-end",
                                marginTop:1.5,
                                width:"450px",
                                "@media screen and (max-width: 47.9375em)" : {
                                    width:1,
                                }
                            }}
                        >
                            <Shbutton  block color={theme.palette.mode === "dark" ? "blue" : "primary"} variant="text" onClick={() => setNfc(false)} startIcon={<MailOutlineIcon />}
                                sx={brand?.color?.button ? {
                                    color: `${theme.palette.mode === "dark" ? brand.color.button.darkText : brand.color.button.light} !important`,
                                    backgroundColor: `rgba(${(theme.palette.mode === "dark" ? brand.color.button.darkTextRgb : brand.color.button.lightRgb).join(',')}, 0.08) !important`,
                                    "&:hover": {
                                        backgroundColor: `rgba(${(theme.palette.mode === "dark" ? brand.color.button.darkTextRgb : brand.color.button.lightRgb).join(',')}, 0.15) !important`
                                    },
                                } : {
                                    backgroundColor: theme.palette.mode === "dark" ? colors.blue[80] : colors.primary[80],
                                    "&:hover": {
                                        backgroundColor: theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100],
                                    },
                                }}
                            >
                                {t("login_with_email")}
                            </Shbutton>
                        </Box>
                    </Box>
                </>
            ) : (
                <>
                    <Box
                        sx={{

                            padding: "32px",
                            borderRadius: "24px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            position: "relative",
                            // boxShadow: "0px 4px 8px rgba(49,65,95,.25)",
                            paddingTop:"40px",
                            "@media screen and (max-width: 47.9375em)": {
                                p:0,
                                px:3,
                            }
                        }}
                    >

                        <Typography variant="h1" fontWeight="1000" fontFamily="Roboto, sans-serif"
                            sx={{
                                color:colors.txt['primary'],
                                textOverflow:"ellipsis",
                                whiteSpace:"nowrap",
                                overflow:"hidden",
                                fontSize:"32px",
                                "@media screen and (max-width: 47.9375em)" : {
                                    fontSize:21
                                }
                            }}
                        >
                            {t("welcome_back")} {process.env.REACT_APP_STOCKHUB_ENV}
                        </Typography>
                        <Typography variant="p" fontWeight="400" fontFamily="Roboto, sans-serif"
                            sx={{
                                color:colors.txt['secondary'],
                                textOverflow:"ellipsis",
                                whiteSpace:"nowrap",
                                overflow:"hidden",
                                fontSize:"17px",
                                marginBottom:5,
                                "@media screen and (max-width: 47.9375em)" : {
                                    fontSize:14,
                                    marginBottom:3,
                                }
                            }}
                        >
                            {t("enter_data_to_start")}
                        </Typography>

                        <Box
                            sx={{
                                "@media screen and (max-width: 47.9375em)" : {
                                    width:1,
                                }
                            }}
                        >
                            {isError &&
                                <Box
                                    sx={{
                                        color:colors.red[400],
                                        background:colors.red[100],
                                        border:`1px solid ${colors.red[400]}`,
                                        p:1,
                                        textAlign:'center',
                                        fontFamily:"Roboto,sans-serif",
                                        mb:4,
                                        borderRadius:5,
                                        fontSize:14,
                                        width:"450px"
                                    }}
                                >
                                    {error?.data?.error}
                                </Box>
                            }
                            <Formik
                                key="login-mode"
                                enableReinitialize
                                onSubmit={handleFormSubmit}
                                initialValues={initialValues}
                                validationSchema={userSchema}>
                                {({values, errors, touched, handleBlur, handleChange, handleSubmit}) => (
                                    <form className="login-form" onSubmit={handleSubmit}>
                                        <Box
                                            className={cx({
                                                'error': !!touched.email && !!errors.email
                                            })}

                                            display="flex"
                                            sx={{
                                                width:"450px",
                                                borderRadius:6,
                                                display:"flex",
                                                alignItems:"center",
                                                background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                                border:theme.palette.mode === "dark" ? null : "1px solid rgba(255,255,255,.15)",
                                                height:48,
                                                pl:2,
                                                "&.error": {
                                                    background:colors.red[100],
                                                    border: `1px solid ${colors.red[400]}`,
                                                    color: colors.red[400]
                                                },
                                                "input:-webkit-autofill,input:-webkit-autofill:hover,input:-webkit-autofill:focus,input:-webkit-autofill:active" : {
                                                    backgroundClip:colors.txt['primary'],
                                                    "webkit-text-fill-color":colors.txt['primary'],
                                                    transition: "background-color 5000s ease-in-out 0s",
                                                    boxShadow:`inset 0 0 20px 20px ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`,
                                                    WebkitTextFillColor: colors.txt['primary'],
                                                    fontSize:16
                                                },
                                                "@media screen and (max-width: 64em)" : {
                                                    height:40,
                                                    borderRadius:5,
                                                    px:1,
                                                },
                                                "@media screen and (max-width: 47.9375em)" : {
                                                    width:1,
                                                }
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    height:32,
                                                    width:32,
                                                    textAlign:"center",
                                                    color: colors.txt['secondary'],
                                                    display:"flex",
                                                    alignItems:"center",
                                                    justifyContent:"center",
                                                }}
                                            >
                                                <MailOutlineIcon
                                                    // color={!!touched.email && !!errors.email ? "red" : "primaryGrey"}

                                                />

                                            </Box>
                                            <InputBase
                                                name="email"
                                                autoFocus={!nfc}
                                                type={"email"}
                                                sx={{
                                                    pl:1.5,
                                                    flex:1,
                                                    color:colors.txt['primary'],
                                                    fontSize:16
                                                }}
                                                placeholder={t("email")}
                                                value={values.email}
                                                onBlur={handleBlur}
                                                onChange={(e, event) => {
                                                    handleChange({ ...event, target: { name: 'email', value: e.target.value } })
                                                }}
                                                error={!!touched.email && !!errors.email}

                                                />
                                        </Box>


                                        <Box
                                            sx={{
                                                width:"450px",
                                                marginTop:2,
                                                width:"450px",
                                                borderRadius:6,
                                                display:"flex",
                                                alignItems:"center",
                                                background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                                border:theme.palette.mode === "dark" ? null : "1px solid rgba(255,255,255,.15)",
                                                height:48,
                                                pl:2,
                                                "&.error": {
                                                    background:colors.red[100],
                                                    border: `1px solid ${colors.red[400]}`,
                                                    color: colors.red[400]
                                                },
                                                "input:-webkit-autofill,input:-webkit-autofill:hover,input:-webkit-autofill:focus,input:-webkit-autofill:active" : {
                                                    backgroundClip:colors.txt['primary'],
                                                    "webkit-text-fill-color":colors.txt['primary'],
                                                    transition: "background-color 5000s ease-in-out 0s",
                                                    boxShadow:`inset 0 0 20px 20px ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`,
                                                    WebkitTextFillColor: colors.txt['primary'],
                                                    fontSize:16
                                                },
                                                "@media screen and (max-width: 64em)" : {
                                                    height:40,
                                                    borderRadius:5,
                                                    px:1,
                                                },
                                                "@media screen and (max-width: 47.9375em)" : {
                                                    width:1,
                                                }
                                            }}

                                            className={cx({
                                                'error': !!touched.password && !!errors.password
                                            })}
                                        >
                                            <Box
                                                sx={{
                                                    height:32,
                                                    width:32,
                                                    textAlign:"center",
                                                    color: colors.txt['secondary'],
                                                    display:"flex",
                                                    alignItems:"center",
                                                    justifyContent:"center",
                                                }}
                                            >
                                                <LockIcon
                                                    // color={!!touched.password && !!errors.password ? "red" : "primaryGrey"}

                                                />

                                            </Box>
                                            <InputBase
                                                name="password"
                                                type={passwordType}
                                                sx={{
                                                    pl:1.5,
                                                    flex:1,
                                                    color:colors.txt['primary'],
                                                    fontSize:16
                                                }}
                                                placeholder={t("password") }
                                                value={values.password}
                                                onBlur={handleBlur}
                                                onChange={(e, event) => {
                                                    handleChange({ ...event, target: { name: 'password', value: e.target.value } })
                                                }}
                                                // onChange={(e) => console.log(e.target.value)}
                                                // error={!!touched.password && !!errors.password}

                                                />
                                                <IconButton
                                                    tabIndex={-1}
                                                    type="button"
                                                    sx={{
                                                        p: 1,
                                                        color:colors.txt['primary']
                                                    }}
                                                    onClick={togglePasswordVisibility}
                                                    // onClick={() => setPasswordType((prev) => (prev === "password" ? "text" : "password"))}
                                                >
                                                    {passwordType === 'password' ? (
                                                        // <DarkModeOutlinedIcon />
                                                        <VisibilityOffIcon />
                                                    ) : (
                                                        <VisibilityIcon/>
                                                        // <LightModeOutlinedIcon />
                                                    )}
                                                    {/* <PersonOutlineIcon color={!!touched.password && !!errors.password ? "red" : "primaryGrey"} /> */}
                                                </IconButton>
                                        </Box>


                                        <Box

                                            sx={{
                                                display:"flex",
                                                justifyContent:"flex-end",
                                                marginTop:2,
                                                width:1,

                                            }}
                                        >
                                            <Shbutton type="submit" disabled={!isOnline} block color={isOnline ? theme.palette.mode === "dark" ? "blue" : "primary" : "red"} variant="contained"
                                                sx={isOnline && brand?.color?.button ? {
                                                    backgroundColor: `${theme.palette.mode === "dark" ? brand.color.button.dark : brand.color.button.light} !important`,
                                                    color: `${theme.palette.mode === "dark" ? brand.color.button.contrastDark : brand.color.button.contrastLight} !important`,
                                                    "&:hover": { backgroundColor: `${theme.palette.mode === "dark" ? brand.color.button.dark : brand.color.button.light} !important` },
                                                } : {}}
                                            >
                                                {t("login")}
                                            </Shbutton>
                                            {/* <Button type="submit" color="primary" variant="contained">
                                                Create new user
                                            </Button> */}
                                        </Box>
                                        <Box

                                            sx={{
                                                display:"flex",
                                                justifyContent:"flex-end",
                                                marginTop:2,
                                                width:1,

                                            }}
                                        >
                                            <Shbutton link={brandSlug ? `/forgot/${brandSlug}` : "/forgot"} disabled={!isOnline} block color={theme.palette.mode === "dark" ? "primaryGreyOpposite" : "primaryGrey"} variant="text">
                                                {t("forgot_password")}
                                            </Shbutton>
                                        </Box>

                                    </form>
                                )}
                            </Formik>
                        </Box>

                        {showNfc &&
                            <>
                                <Box
                                    sx={{
                                        display:"flex",
                                        alignItems:"center",
                                        gap:2,
                                        marginTop:4,
                                        width:"450px",
                                        "@media screen and (max-width: 47.9375em)" : {
                                            width:1,
                                            mt:2,
                                            display:'none',
                                        }
                                    }}
                                >
                                    <Box sx={{ flex:1, height:"1px", background:theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[200] }} />
                                    <Text light>{t("or_login_with")}</Text>
                                    <Box sx={{ flex:1, height:"1px", background:theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[200] }} />
                                </Box>
                                <Box
                                    sx={{
                                        display:"flex",
                                        justifyContent:"flex-end",
                                        marginTop:1.5,
                                        width:"450px",
                                        "@media screen and (max-width: 47.9375em)" : {
                                            width:1,
                                            mt:3,
                                        }
                                    }}
                                >
                                    <Shbutton block color={theme.palette.mode === "dark" ? "blue" : "primary"} variant="text" onClick={() => setNfc(true)} startIcon={<WifiTetheringOutlinedIcon />}
                                        sx={brand?.color?.button ? {
                                            color: `${theme.palette.mode === "dark" ? brand.color.button.darkText : brand.color.button.light} !important`,
                                            backgroundColor: `rgba(${(theme.palette.mode === "dark" ? brand.color.button.darkTextRgb : brand.color.button.lightRgb).join(',')}, 0.08) !important`,
                                            "&:hover": {
                                                backgroundColor: `rgba(${(theme.palette.mode === "dark" ? brand.color.button.darkTextRgb : brand.color.button.lightRgb).join(',')}, 0.15) !important`
                                            },
                                        } : {
                                            backgroundColor: theme.palette.mode === "dark" ? colors.blue[80] : colors.primary[80],
                                            "&:hover": {
                                                backgroundColor: theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100],
                                            },
                                        }}
                                    >
                                    {t("login_with_nfc")}
                                    </Shbutton>
                                </Box>
                            </>
                        }
                    </Box>

                </>
            )}
        </>
    )
}

export default Login;
